import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OnboardingLayout } from '../Layout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { CountryPicker } from '@/components/domain/CountryPicker';
import {
  useAutoBookingSettings,
  useUpdateAutoBookingSettings,
} from '@/api/queries/user';
import { useTriggerEventTypes } from '@/api/queries/reference';
import { useCountries } from '@/api/queries/reference';
import { paths } from '@/routes/paths';
import type { EvacuationTriggerCode } from '@/api/types';
import { Card } from '@/components/ui/Card';

export function OnboardingEvacuationPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = useAutoBookingSettings();
  const triggers = useTriggerEventTypes();
  const countries = useCountries();
  const update = useUpdateAutoBookingSettings();

  const [home, setHome] = useState<string>('');
  const [trigs, setTrigs] = useState<EvacuationTriggerCode[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (settings.data?.home_country_code) setHome(settings.data.home_country_code);
    if (settings.data?.evacuation_triggers) setTrigs(settings.data.evacuation_triggers);
  }, [settings.data]);

  const homeName = home ? countries.data?.find((c) => c.code === home)?.name ?? home : '';

  const submit = async () => {
    await update.mutateAsync({
      home_country_code: home || undefined,
      evacuation_triggers: trigs,
      enabled: true,
    });
    navigate(paths.onboardingEvacuationCountries);
  };

  return (
    <OnboardingLayout back title={t('onboarding.evacuation.title')}>
      <Input
        label={t('onboarding.evacuation.home_country')}
        value={homeName}
        readOnly
        placeholder="—"
        onClick={() => setPickerOpen(true)}
      />

      <Card variant="light">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>{t('onboarding.evacuation.triggers')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(triggers.data ?? []).map((tr) => (
            <Toggle
              key={tr.code}
              label={t(`triggers.${tr.code}`, { defaultValue: tr.label_en })}
              checked={trigs.includes(tr.code as EvacuationTriggerCode)}
              onChange={(e) => {
                setTrigs((cur) =>
                  e.target.checked
                    ? [...cur, tr.code as EvacuationTriggerCode]
                    : cur.filter((c) => c !== tr.code),
                );
              }}
            />
          ))}
        </div>
      </Card>

      <Button block size="lg" variant="inverted" onClick={submit} loading={update.isPending}>
        {t('common.continue')}
      </Button>

      <BottomSheet open={pickerOpen} onClose={() => setPickerOpen(false)} title={t('onboarding.evacuation.home_country')}>
        <CountryPicker
          value={home}
          onPick={(code) => {
            setHome(code);
            setPickerOpen(false);
          }}
        />
      </BottomSheet>
    </OnboardingLayout>
  );
}
